// FleetDetailPage — /fleet/:deviceId. Device detail header + two tabs:
//   • Terminal  — xterm.js spliced onto the relay's …/terminal WS (tmux PTY).
//   • Inference — a chat console over the relay's …/infer WS.
//
// Both legs speak the Fleet Remote CLI wire protocol (see api/fleetWire.ts,
// which mirrors server-go/agent_relay.go + fleet_terminal.go). Auth is the
// operator session cookie (same-origin, sent automatically on the WS upgrade).
//
// The relay refuses pre-upgrade with a JSON 409 when the kit has no live
// channel / no pinned device key / is not enrolled; we surface that reason. The
// relay also assigns at most one read-write terminal session per kit and
// attaches further operators as read-only followers — we show a banner and
// disable input in that case.

import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Terminal } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import "@xterm/xterm/css/xterm.css";
import { api, ApiError } from "@/api/client";
import { useMe, registerReceipt } from "@/api/hooks";
import type { RegisterReceiptResponse } from "@/api/hooks";
import {
  CtrlFrame,
  FRAME_KIND_INFER,
  FRAME_KIND_PTY,
  arrayBufferToUint8,
  decodeDataFrame,
  decodeUtf8,
  encodeDataFrame,
  encodeUtf8,
  estimateTokens,
  probeRefusalReason,
  tokenUsageFromArc,
  wsURL,
} from "@/api/fleetWire";

// --- fleet list types (mirror of /api/v1/fleet — api_admin.go handleAPIFleet)
// NOTE: the fleet API does NOT currently expose channel-liveness or a
// has-device-key flag. We derive "has key" best-effort from the connect attempt
// (the relay's 409 reason is the source of truth). See the report's Go-gap flag.
type Cohort = "stable" | "canary";
// DeviceMode mirrors the Go DeviceRecord.Mode enum: "onkit" (model on the kit,
// WS relay) | "hostgated" (model on a host, device gates per-response via the
// ARC serving shim). Absent => onkit (pre-feature records).
type DeviceMode = "onkit" | "hostgated";

interface FleetRow {
  device_id: string;
  cohort: Cohort;
  mode?: DeviceMode;
  enrolled_at?: string;
  last_auth_at?: string;
  description?: string;
  last_telemetry?: unknown;
}

interface FleetResponse {
  items: FleetRow[];
  fleet_size: number;
}

type Tab = "terminal" | "inference";

export function FleetDetailPage() {
  const { deviceId = "" } = useParams();
  const me = useMe();
  const [tab, setTab] = useState<Tab>("terminal");

  // The detail header reuses the fleet list (no per-device GET exists yet); we
  // pluck this device's row out of it.
  const fleet = useQuery<FleetResponse>({
    queryKey: ["fleet"],
    queryFn: () => api<FleetResponse>("/api/v1/fleet"),
    refetchInterval: 30_000,
  });

  const row = useMemo(
    () => fleet.data?.items.find((d) => d.device_id === deviceId),
    [fleet.data, deviceId],
  );

  const isOperatorPlus = me.data?.role === "operator" || me.data?.role === "admin";

  if (fleet.isLoading) {
    return <p className="text-slate-500 dark:text-slate-400">Loading…</p>;
  }
  if (fleet.error) {
    if (fleet.error instanceof ApiError && fleet.error.status === 401) {
      return (
        <div className="rounded bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 px-3 py-2 text-sm">
          Sign in as an operator to view this device.
        </div>
      );
    }
    return (
      <div className="rounded bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 px-3 py-2 text-sm text-red-700 dark:text-red-300">
        Could not load fleet: {(fleet.error as Error).message}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <Link to="/fleet" className="text-sm text-blue-700 dark:text-blue-400 hover:underline">
          ← back to fleet
        </Link>
      </div>

      <DeviceHeader deviceId={deviceId} row={row} />

      {!isOperatorPlus ? (
        <div className="rounded bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 px-3 py-2 text-sm">
          The remote CLI requires the operator role. You are signed in as{" "}
          <span className="font-mono">{me.data?.role ?? "guest"}</span>.
        </div>
      ) : !row ? (
        <div className="rounded bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 px-3 py-2 text-sm">
          Device <span className="font-mono">{deviceId}</span> is not enrolled. The remote CLI
          is only available for enrolled devices with a pinned hardware key.
        </div>
      ) : (
        <>
          <div className="border-b border-slate-200 dark:border-slate-700 flex gap-1 text-sm">
            <TabButton active={tab === "terminal"} onClick={() => setTab("terminal")}>
              Terminal
            </TabButton>
            <TabButton active={tab === "inference"} onClick={() => setTab("inference")}>
              Inference
            </TabButton>
          </div>

          {/* Both tabs stay mounted so a backgrounded terminal/chat session is
              not torn down when the operator flips tabs; we just hide the
              inactive one. */}
          <div className={tab === "terminal" ? "" : "hidden"}>
            <TerminalTab deviceId={deviceId} active={tab === "terminal"} />
          </div>
          <div className={tab === "inference" ? "" : "hidden"}>
            {/* Branch the inference console on the device's mode: on-kit keeps
                the WS relay + tok/s telemetry; host-gated proxies a single POST
                to the ARC serving shim and renders the answer + arc block. */}
            {(row?.mode ?? "onkit") === "hostgated" ? (
              <HostgatedInferenceTab deviceId={deviceId} />
            ) : (
              <InferenceTab deviceId={deviceId} />
            )}
          </div>
        </>
      )}
    </div>
  );
}

// --- device header ----------------------------------------------------------

function DeviceHeader({ deviceId, row }: { deviceId: string; row?: FleetRow }) {
  return (
    <div>
      <h1 className="text-xl font-semibold">
        Device <span className="font-mono">{deviceId}</span>
      </h1>
      <dl className="mt-2 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-4 gap-y-2 text-sm">
        <Field label="Cohort">
          {row ? (
            <span
              className={`rounded px-2 py-0.5 text-xs font-semibold uppercase ${
                row.cohort === "canary"
                  ? "bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300"
                  : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300"
              }`}
            >
              {row.cohort}
            </span>
          ) : (
            <span className="text-slate-400">—</span>
          )}
        </Field>
        <Field label="Mode">
          {row ? (
            <span
              className={`rounded px-2 py-0.5 text-xs font-semibold ${
                (row.mode ?? "onkit") === "hostgated"
                  ? "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300"
                  : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300"
              }`}
              title={
                (row.mode ?? "onkit") === "hostgated"
                  ? "Host-gated: the model runs on a host; this device guards the response via the ARC serving shim."
                  : "On-kit: the model runs on the device (on-die generation)."
              }
            >
              {(row.mode ?? "onkit") === "hostgated" ? "host-gated" : "on-kit"}
            </span>
          ) : (
            <span className="text-slate-400">—</span>
          )}
        </Field>
        <Field label="Enrolled">
          <span className="font-mono text-xs">{row?.enrolled_at ?? "—"}</span>
        </Field>
        <Field label="Last auth">
          <span className="font-mono text-xs">{row?.last_auth_at ?? "—"}</span>
        </Field>
        <Field label="Last telemetry">
          <span className="font-mono text-xs">{row?.last_telemetry ? "✓ on file" : "—"}</span>
        </Field>
        <Field label="Hardware key">
          {/* The fleet API does not (yet) expose whether a device pubkey is
              pinned, so we cannot show this authoritatively here. The relay
              enforces it at connect time — the Terminal/Inference tabs report
              "CLI unavailable (no device key)" if it is missing. */}
          <span className="text-xs text-slate-500 dark:text-slate-400" title="The relay verifies the pinned ECDSA device key at connect time; see the Terminal tab.">
            verified at connect
          </span>
        </Field>
      </dl>
      {row?.description && (
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">{row.description}</p>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</dt>
      <dd className="mt-0.5">{children}</dd>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={
        "px-3 py-2 -mb-px border-b-2 transition-colors " +
        (active
          ? "border-slate-900 dark:border-purple-500 font-medium text-slate-900 dark:text-white"
          : "border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200")
      }
    >
      {children}
    </button>
  );
}

// --- connection status shared by both tabs ----------------------------------

type ConnState =
  | { kind: "idle" }
  | { kind: "connecting" }
  | { kind: "open"; readOnly: boolean }
  | { kind: "unavailable"; reason: string }
  | { kind: "closed"; reason: string };

function StatusPill({ state }: { state: ConnState }) {
  const map: Record<ConnState["kind"], { label: string; cls: string }> = {
    idle: { label: "idle", cls: "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300" },
    connecting: { label: "connecting…", cls: "bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300" },
    open: { label: "live", cls: "bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300" },
    unavailable: { label: "CLI unavailable", cls: "bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300" },
    closed: { label: "disconnected", cls: "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300" },
  };
  const m = map[state.kind];
  return (
    <span className={`rounded px-2 py-0.5 text-xs font-semibold ${m.cls}`}>{m.label}</span>
  );
}

// ===========================================================================
// Terminal tab
// ===========================================================================

function TerminalTab({ deviceId, active }: { deviceId: string; active: boolean }) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const termRef = useRef<Terminal | null>(null);
  const fitRef = useRef<FitAddon | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const readOnlyRef = useRef(false);
  const [state, setState] = useState<ConnState>({ kind: "idle" });
  // A nonce the operator bumps to force a reconnect after a close.
  const [reconnectTick, setReconnectTick] = useState(0);

  const termPath = `/api/v1/fleet/${encodeURIComponent(deviceId)}/terminal`;

  // Build the xterm instance once (on mount); the WS lifecycle is a separate
  // effect keyed on reconnectTick.
  useEffect(() => {
    const term = new Terminal({
      cursorBlink: true,
      convertEol: false,
      fontFamily:
        'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
      fontSize: 13,
      scrollback: 5000,
      theme: { background: "#0f172a", foreground: "#e2e8f0" }, // slate-900 / slate-200
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    if (hostRef.current) term.open(hostRef.current);
    termRef.current = term;
    fitRef.current = fit;
    try {
      fit.fit();
    } catch {
      /* host not laid out yet */
    }

    return () => {
      term.dispose();
      termRef.current = null;
      fitRef.current = null;
    };
  }, []);

  // Refit when the tab becomes visible (xterm cannot measure a display:none
  // host, so the initial fit while hidden is a no-op) and on window resize.
  useEffect(() => {
    if (!active) return;
    const refit = () => {
      const fit = fitRef.current;
      const term = termRef.current;
      const ws = wsRef.current;
      if (!fit || !term) return;
      try {
        fit.fit();
      } catch {
        return;
      }
      if (ws && ws.readyState === WebSocket.OPEN && !readOnlyRef.current) {
        ws.send(
          JSON.stringify({ t: "pty_resize", cols: term.cols, rows: term.rows } satisfies CtrlFrame),
        );
      }
    };
    // Defer one frame so the now-visible host has its layout.
    const id = window.requestAnimationFrame(refit);
    window.addEventListener("resize", refit);
    return () => {
      window.cancelAnimationFrame(id);
      window.removeEventListener("resize", refit);
    };
  }, [active]);

  // WS lifecycle.
  useEffect(() => {
    const term = termRef.current;
    if (!term) return;

    let disposed = false;
    setState({ kind: "connecting" });
    readOnlyRef.current = false;

    // Open with the current size so the relay's pty_open uses real dims.
    const cols = term.cols || 80;
    const rows = term.rows || 24;
    const ws = new WebSocket(
      wsURL(termPath, { cols: String(cols), rows: String(rows) }),
    );
    ws.binaryType = "arraybuffer";
    wsRef.current = ws;

    // Browser→relay PTY stdin. The relay overwrites the sid with the session's
    // own (fleet_terminal.go ~L426), so sid 0 here is fine. Dropped server-side
    // for read-only followers, but we also gate locally for immediate feedback.
    const onDataDisp = term.onData((data) => {
      if (readOnlyRef.current) return;
      if (ws.readyState !== WebSocket.OPEN) return;
      ws.send(encodeDataFrame(FRAME_KIND_PTY, 0, encodeUtf8(data)));
    });

    ws.onmessage = (ev) => {
      if (typeof ev.data === "string") {
        // Control frame.
        let f: CtrlFrame;
        try {
          f = JSON.parse(ev.data) as CtrlFrame;
        } catch {
          return;
        }
        switch (f.t) {
          case "ready": {
            const ro = (f.reason ?? "").includes("read-only");
            readOnlyRef.current = ro;
            setState({ kind: "open", readOnly: ro });
            // Send an explicit resize on open so the kit matches our viewport
            // exactly (pty_open already carried cols/rows for the RW holder).
            if (!ro && ws.readyState === WebSocket.OPEN) {
              ws.send(
                JSON.stringify({
                  t: "pty_resize",
                  cols: term.cols,
                  rows: term.rows,
                } satisfies CtrlFrame),
              );
            }
            term.focus();
            break;
          }
          case "deny":
            setState({ kind: "closed", reason: f.reason || "session denied" });
            term.writeln(`\r\n\x1b[33m[session ended: ${f.reason || "denied"}]\x1b[0m`);
            break;
          case "pong":
            break;
          default:
            break;
        }
        return;
      }
      // Binary frame: PTY stdout (0x01).
      const bytes = arrayBufferToUint8(ev.data as ArrayBuffer);
      const fr = decodeDataFrame(bytes);
      if (!fr) return;
      if (fr.kind === FRAME_KIND_PTY) {
        term.write(fr.payload);
      }
    };

    ws.onclose = () => {
      if (disposed) return;
      setState((prev) =>
        // Preserve a more specific terminal state (unavailable / deny) if set.
        prev.kind === "unavailable" || prev.kind === "closed"
          ? prev
          : { kind: "closed", reason: "connection closed" },
      );
    };

    ws.onerror = () => {
      // A failed upgrade does not expose the HTTP body; probe for the 409 reason.
      void probeRefusalReason(termPath).then((reason) => {
        if (disposed) return;
        if (reason) setState({ kind: "unavailable", reason });
      });
    };

    return () => {
      disposed = true;
      onDataDisp.dispose();
      wsRef.current = null;
      try {
        ws.close();
      } catch {
        /* already closing */
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, reconnectTick]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <StatusPill state={state} />
        {state.kind === "open" && state.readOnly && (
          <span className="rounded bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-2 py-0.5 text-xs font-semibold">
            read-only (another operator holds the session)
          </span>
        )}
        {(state.kind === "closed" || state.kind === "unavailable") && (
          <button
            onClick={() => setReconnectTick((n) => n + 1)}
            className="text-xs rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 hover:bg-slate-100 dark:hover:bg-slate-700"
          >
            reconnect
          </button>
        )}
        <span className="text-xs text-slate-500 dark:text-slate-400 ml-auto">
          tmux session <span className="font-mono">fleet</span> · exclusive lock; extra operators attach read-only
        </span>
      </div>

      {state.kind === "unavailable" && (
        <div className="rounded bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          <p className="font-medium">CLI unavailable</p>
          <p className="mt-0.5">{state.reason}</p>
          <p className="mt-1 text-xs text-red-600 dark:text-red-400">
            The remote terminal needs a live kit channel and a pinned hardware (ECDSA) device
            key. Enroll the device's public key and confirm its fleet agent is connected.
          </p>
        </div>
      )}

      {state.kind === "open" && state.readOnly && (
        <div className="rounded bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
          You are attached as a <strong>read-only follower</strong>. Another operator holds the
          read-write lock for this kit; your keystrokes are ignored until they disconnect and you
          reconnect.
        </div>
      )}

      <div
        className={
          "rounded border border-slate-200 dark:border-slate-700 bg-[#0f172a] p-2 overflow-hidden " +
          (state.kind === "open" && state.readOnly ? "opacity-80" : "")
        }
      >
        {/* xterm renders into this host. Fixed height keeps the viewport stable;
            FitAddon maps it to cols/rows. */}
        <div
          ref={hostRef}
          className="h-[28rem] w-full"
          aria-label="device terminal"
          // Block focus (and thus keystrokes) when read-only; xterm's textarea
          // sits inside, so making the host non-interactive is a clear visual +
          // functional cue on top of the server-side drop.
          style={readOnlyRef.current ? { pointerEvents: "none" } : undefined}
        />
      </div>
    </div>
  );
}

// ===========================================================================
// Inference tab
// ===========================================================================

interface ChatTurn {
  role: "user" | "assistant";
  text: string;
  // Parsed `arc` verdict block (if the kit emitted one), and the final status.
  arc?: unknown;
  status?: number;
  pending?: boolean;
  // Live inference telemetry for an assistant turn. All timings are exact
  // client-side wall-clock (performance.now() deltas, in ms). The token count is
  // an ESTIMATE from streamed characters unless `tokensExact` is set (the daemon
  // does not pass an exact count on the stream today — see fleetWire.ts).
  metrics?: TurnMetrics;
  // Receipt-transparency registration lifecycle (only set when the toggle is ON
  // and this turn carried a signed arc.receipt). Drives the inline status line.
  register?: RegisterState;
}

// RegisterState tracks one reply's arc.receipt as it travels reply → signed
// receipt → registered in the SA store → verified in /receipts.
type RegisterState =
  | { phase: "registering" }
  | { phase: "done"; result: RegisterReceiptResponse }
  | { phase: "error"; message: string };

// extractReceipt pulls the self-contained signed bundle out of a parsed `arc`
// block. The daemon emits `arc.receipt` as the merge-bundle (or null when the
// attest hardware was unavailable off-kit). Returns null when there is nothing
// signed to register (so the toggle silently no-ops on an unsigned reply).
function extractReceipt(arc: unknown): unknown | null {
  if (!arc || typeof arc !== "object") return null;
  const r = (arc as { receipt?: unknown }).receipt;
  if (!r || typeof r !== "object") return null;
  const rec = r as Record<string, unknown>;
  if (typeof rec.session_hash === "string" && typeof rec.hmac_signature === "string") {
    return r;
  }
  return null;
}

interface TurnMetrics {
  startMs: number; // performance.now() at request send
  ttftMs?: number; // elapsed send→first content chunk (exact)
  endMs?: number; // elapsed send→http_end (exact, total wall time)
  chars: number; // streamed content characters (drives the token estimate)
  tokensExact?: number; // exact count IFF the stream ever carries usage; else undefined
}

function InferenceTab({ deviceId }: { deviceId: string }) {
  const inferPath = `/api/v1/fleet/${encodeURIComponent(deviceId)}/infer`;
  const wsRef = useRef<WebSocket | null>(null);
  const [state, setState] = useState<ConnState>({ kind: "idle" });
  const [prompt, setPrompt] = useState("");
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [reconnectTick, setReconnectTick] = useState(0);
  // Receipt-transparency toggle (default OFF — opt-in for demos). When ON, each
  // reply's signed arc.receipt is POSTed to the SA receipt store so the full
  // attestation lifecycle (generate → on-die signed receipt → registered →
  // verifiable in /receipts) is demonstrable. Persisted per-browser so a demo
  // keeps the choice across reloads; a ref mirrors it for the async finish
  // handler (which closes over a stale value otherwise).
  const [registerReceipts, setRegisterReceipts] = useState<boolean>(
    () => localStorage.getItem("animus.registerReceipts") === "1",
  );
  const registerRef = useRef(registerReceipts);
  useEffect(() => {
    registerRef.current = registerReceipts;
    localStorage.setItem("animus.registerReceipts", registerReceipts ? "1" : "0");
  }, [registerReceipts]);
  // Accumulator for the in-flight assistant turn's raw SSE bytes.
  const sseBufRef = useRef("");
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // A monotonically-bumped tick that re-renders the live timer / tok-s while a
  // turn is generating. Driven by a 200ms interval that only runs when pending.
  const [, setNowTick] = useState(0);
  const generating = turns.some((t) => t.pending);

  // WS lifecycle. The infer leg sends NO "ready" frame (fleet_terminal.go
  // handleFleetInfer goes straight to runInfer), so socket-open === ready.
  useEffect(() => {
    let disposed = false;
    setState({ kind: "connecting" });
    const ws = new WebSocket(wsURL(inferPath));
    ws.binaryType = "arraybuffer";
    wsRef.current = ws;

    ws.onopen = () => {
      if (!disposed) setState({ kind: "open", readOnly: false });
    };

    ws.onmessage = (ev) => {
      if (typeof ev.data === "string") {
        let f: CtrlFrame;
        try {
          f = JSON.parse(ev.data) as CtrlFrame;
        } catch {
          return;
        }
        if (f.t === "http_end") {
          finishAssistantTurn(f.status);
        }
        // ("pong"/"deny" possible; deny would also close the socket.)
        if (f.t === "deny") {
          setState({ kind: "closed", reason: f.reason || "session denied" });
        }
        return;
      }
      // Binary: 0x02 inference chunk (raw SSE bytes, passed through unchanged).
      const bytes = arrayBufferToUint8(ev.data as ArrayBuffer);
      const fr = decodeDataFrame(bytes);
      if (!fr || fr.kind !== FRAME_KIND_INFER) return;
      appendSSE(decodeUtf8(fr.payload));
    };

    ws.onclose = () => {
      if (disposed) return;
      setState((prev) =>
        prev.kind === "unavailable" || prev.kind === "closed"
          ? prev
          : { kind: "closed", reason: "connection closed" },
      );
    };
    ws.onerror = () => {
      void probeRefusalReason(inferPath).then((reason) => {
        if (disposed) return;
        if (reason) setState({ kind: "unavailable", reason });
      });
    };

    return () => {
      disposed = true;
      wsRef.current = null;
      try {
        ws.close();
      } catch {
        /* already closing */
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, reconnectTick]);

  useEffect(() => {
    // Keep the transcript scrolled to the latest content.
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [turns]);

  // Live elapsed timer + live tok/s: while a turn is generating, re-render ~5×/s
  // so the elapsed time and running tok/s update smoothly. The displayed values
  // are derived from performance.now() at render (exact), not counted here — the
  // interval just forces the refresh. It stops as soon as nothing is pending, so
  // a slow canary reply (~0.2 tok/s, ~2 min) keeps ticking without looking hung.
  useEffect(() => {
    if (!generating) return;
    const id = window.setInterval(() => setNowTick((n) => n + 1), 200);
    return () => window.clearInterval(id);
  }, [generating]);

  // appendSSE parses the kit daemon's OpenAI-style SSE stream incrementally and
  // appends decoded deltas to the pending assistant turn. The stream is
  // "data: {json}\n\n" lines, terminated by "data: [DONE]". We tolerate partial
  // lines across chunks via sseBufRef. If a chunk carries a top-level `arc`
  // verdict object (the kit's safety block), we stash it on the turn.
  function appendSSE(chunk: string) {
    sseBufRef.current += chunk;
    const parts = sseBufRef.current.split("\n");
    // Keep the last (possibly partial) line in the buffer.
    sseBufRef.current = parts.pop() ?? "";
    let deltaText = "";
    let arc: unknown;
    let exactTokens: number | null = null;
    for (const raw of parts) {
      const line = raw.trim();
      if (!line || line.startsWith(":")) continue; // comment / keep-alive
      if (!line.startsWith("data:")) continue;
      const payload = line.slice("data:".length).trim();
      if (payload === "[DONE]") continue;
      try {
        const obj = JSON.parse(payload) as Record<string, unknown>;
        // OpenAI-ish: choices[0].delta.content
        const choices = obj.choices as
          | Array<{ delta?: { content?: string }; text?: string }>
          | undefined;
        if (choices && choices[0]) {
          deltaText += choices[0].delta?.content ?? choices[0].text ?? "";
        } else if (typeof obj.content === "string") {
          deltaText += obj.content;
        }
        if (obj.arc !== undefined) arc = obj.arc;
        // Prefer an exact token count if the stream ever carries one (top-level
        // `usage`, or nested on the `arc` block). None today — stays an estimate.
        const ex = tokenUsageFromArc(obj) ?? tokenUsageFromArc(obj.arc);
        if (ex !== null) exactTokens = ex;
      } catch {
        // Not JSON — surface the raw line (some endpoints stream plain text).
        deltaText += payload;
      }
    }
    if (deltaText || arc !== undefined || exactTokens !== null) {
      // TTFT is the exact send→first-content wall-clock; stamp it once, when the
      // first non-empty content delta lands (not on the role-only opening chunk).
      const firstContentAt = deltaText ? performance.now() : undefined;
      setTurns((prev) => patchPending(prev, (t) => ({
        ...t,
        text: t.text + deltaText,
        arc: arc !== undefined ? arc : t.arc,
        metrics: t.metrics && {
          ...t.metrics,
          chars: t.metrics.chars + deltaText.length,
          ttftMs:
            t.metrics.ttftMs ??
            (firstContentAt !== undefined
              ? firstContentAt - t.metrics.startMs
              : undefined),
          tokensExact: exactTokens !== null ? exactTokens : t.metrics.tokensExact,
        },
      })));
    }
  }

  function finishAssistantTurn(status?: number) {
    // Stamp the exact total wall-clock (send→http_end) at the moment the end
    // frame arrives, before flushing the tail (the flush is cheap but we want
    // the timing to reflect when the kit actually signalled completion).
    const doneAt = performance.now();
    // Flush any buffered trailing line through the parser first.
    if (sseBufRef.current) {
      const tail = sseBufRef.current;
      sseBufRef.current = "";
      appendSSE(tail + "\n");
    }
    // After the flush, decide whether to register this reply's receipt: only if
    // the toggle is ON and the now-complete turn carries a signed arc.receipt.
    // We compute the target turn index inside the state update so it's stable
    // for the async patch that follows (turns only ever append here).
    let registerIdx = -1;
    let receipt: unknown = null;
    setTurns((prev) => {
      let idx = -1;
      for (let i = prev.length - 1; i >= 0; i--) {
        if (prev[i].role === "assistant" && prev[i].pending) {
          idx = i;
          break;
        }
      }
      if (idx < 0) return prev;
      const rec = registerRef.current ? extractReceipt(prev[idx].arc) : null;
      if (rec) {
        registerIdx = idx;
        receipt = rec;
      }
      const next = prev.slice();
      next[idx] = {
        ...next[idx],
        pending: false,
        status,
        metrics: next[idx].metrics && {
          ...next[idx].metrics!,
          endMs: doneAt - next[idx].metrics!.startMs,
        },
        register: rec ? { phase: "registering" } : next[idx].register,
      };
      return next;
    });
    sseBufRef.current = "";
    if (registerIdx >= 0 && receipt) {
      void doRegisterReceipt(registerIdx, receipt);
    }
  }

  // doRegisterReceipt POSTs one reply's arc.receipt to the SA transparency log
  // and patches that turn's register state with the result (id + verify status)
  // or a clear inline error. Never throws — a failed register must not break the
  // chat. The turn is addressed by its stable index (turns only append).
  async function doRegisterReceipt(idx: number, receipt: unknown) {
    try {
      // Pass the inference console's device_id so the SA ECDSA-verifies the
      // receipt against THIS kit's enrollment-pinned pubkey (device-rooted proof).
      const result = await registerReceipt(receipt, deviceId);
      setTurns((prev) => {
        if (idx >= prev.length) return prev;
        const next = prev.slice();
        next[idx] = { ...next[idx], register: { phase: "done", result } };
        return next;
      });
    } catch (e) {
      const message =
        e instanceof ApiError
          ? e.status === 401 || e.status === 403
            ? "sign in as an operator to register receipts"
            : `register failed (HTTP ${e.status})`
          : e instanceof Error
          ? e.message
          : "register failed";
      setTurns((prev) => {
        if (idx >= prev.length) return prev;
        const next = prev.slice();
        next[idx] = { ...next[idx], register: { phase: "error", message } };
        return next;
      });
    }
  }

  function send() {
    const text = prompt.trim();
    const ws = wsRef.current;
    if (!text || !ws || ws.readyState !== WebSocket.OPEN) return;
    // Build the kit daemon's /silicon/chat body (OpenAI-ish, streaming). The
    // relay forwards `body` verbatim as the request body string.
    const body = JSON.stringify({
      messages: [{ role: "user", content: text }],
      stream: true,
    });
    setTurns((prev) => [
      ...prev,
      { role: "user", text },
      {
        role: "assistant",
        text: "",
        pending: true,
        // Exact wall-clock origin for every metric on this turn.
        metrics: { startMs: performance.now(), chars: 0 },
      },
    ]);
    sseBufRef.current = "";
    ws.send(
      JSON.stringify({
        t: "http",
        method: "POST",
        path: "/silicon/chat",
        body,
      } satisfies CtrlFrame),
    );
    setPrompt("");
  }

  const canSend = state.kind === "open" && !generating;
  // The in-flight assistant turn (if any) drives the live telemetry strip.
  const liveTurn = generating
    ? turns.find((t) => t.role === "assistant" && t.pending)
    : undefined;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <StatusPill state={state} />
        {(state.kind === "closed" || state.kind === "unavailable") && (
          <button
            onClick={() => setReconnectTick((n) => n + 1)}
            className="text-xs rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 hover:bg-slate-100 dark:hover:bg-slate-700"
          >
            reconnect
          </button>
        )}
        <span className="text-xs text-slate-500 dark:text-slate-400 ml-auto">
          relayed to the kit's <span className="font-mono">/silicon/chat</span>
        </span>
      </div>

      <div className="rounded bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 px-3 py-2 text-xs text-blue-800 dark:text-blue-300">
        Safety runs in <strong>attestation mode</strong> here: the <span className="font-mono">arc</span>{" "}
        verdict is <strong>advisory</strong> (it does not block generation) until Model-A. For
        demo / test use.
      </div>

      <RegisterToggle enabled={registerReceipts} onChange={setRegisterReceipts} />

      {liveTurn?.metrics && <LiveTelemetry m={liveTurn.metrics} />}

      {state.kind === "unavailable" && (
        <div className="rounded bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          <p className="font-medium">CLI unavailable</p>
          <p className="mt-0.5">{state.reason}</p>
        </div>
      )}

      <div
        ref={scrollRef}
        className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3 h-[24rem] overflow-y-auto space-y-3 text-sm"
      >
        {turns.length === 0 && (
          <p className="text-slate-400 dark:text-slate-500 text-center py-12">
            No messages yet. Send a prompt below to run inference on this kit.
          </p>
        )}
        {turns.map((t, i) => (
          <ChatBubble key={i} turn={t} />
        ))}
      </div>

      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
      >
        <input
          type="text"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder={
            state.kind === "open" ? "Ask the kit something…" : "Connect to send a prompt…"
          }
          disabled={state.kind !== "open"}
          className="flex-1 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={!canSend || !prompt.trim()}
          className="rounded bg-slate-900 dark:bg-purple-600 text-white px-4 py-2 text-sm font-medium hover:bg-slate-800 dark:hover:bg-purple-500 disabled:opacity-40 inline-flex items-center gap-2"
        >
          {generating ? (
            <>
              <Spinner />
              <span>generating…</span>
            </>
          ) : (
            "Send"
          )}
        </button>
      </form>
    </div>
  );
}

// ===========================================================================
// Host-gated inference tab (Strategy A)
// ===========================================================================

// In host-gated mode the model runs on a HOST and this device is the per-
// response safety gate + attestor. The SA proxies a single non-streaming POST
// to /api/v1/fleet/{id}/infer (fleet_hostgated.go), which forwards the prompt to
// the ARC serving shim's /api/generate; the shim generates upstream, gates the
// full response through this device's silicon, and returns the (possibly
// refused) answer plus an `arc` metadata object (gate verdict, attestation
// receipt, and pcache/cache stats when present). We render the answer + that
// arc block. There is no token-by-token stream here (gating is per-response), so
// unlike the on-kit tab there is no live tok/s telemetry.

// HostgatedArc is the shape we read off the shim's `arc` block. All fields are
// optional/forward-compatible — the shim/daemon define the authoritative shape;
// we only pull what we display and keep the raw JSON for transparency.
interface HostgatedArc {
  gate_backend?: string;
  blocked?: boolean;
  decision?: string;
  receipt?: unknown;
  pcache?: { skip_pct?: number; speedup?: number } & Record<string, unknown>;
  windowed_nll?: number | null;
  nll_prefilter?: boolean;
  [k: string]: unknown;
}

interface HostgatedReply {
  text: string;
  arc?: HostgatedArc;
  status?: number;
  wallMs?: number;
}

interface HostgatedTurn {
  role: "user" | "assistant";
  text: string;
  arc?: HostgatedArc;
  pending?: boolean;
  error?: string;
  wallMs?: number;
}

function HostgatedInferenceTab({ deviceId }: { deviceId: string }) {
  const inferPath = `/api/v1/fleet/${encodeURIComponent(deviceId)}/infer`;
  const [prompt, setPrompt] = useState("");
  const [turns, setTurns] = useState<HostgatedTurn[]>([]);
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [turns]);

  async function send() {
    const text = prompt.trim();
    if (!text || busy) return;
    setBusy(true);
    setPrompt("");
    const startMs = performance.now();
    setTurns((prev) => [
      ...prev,
      { role: "user", text },
      { role: "assistant", text: "", pending: true },
    ]);
    try {
      // Non-streaming POST proxied to the shim. The Go proxy returns the shim's
      // JSON verbatim: { response, done, arc: {...} }.
      const reply = await api<{ response?: string; message?: { content?: string }; arc?: HostgatedArc }>(
        inferPath,
        { method: "POST", body: { prompt: text } },
      );
      const answer = reply.response ?? reply.message?.content ?? "";
      patchLastAssistant(setTurns, {
        text: answer,
        arc: reply.arc,
        wallMs: performance.now() - startMs,
      });
    } catch (e) {
      const message =
        e instanceof ApiError
          ? e.status === 401 || e.status === 403
            ? "sign in as an operator to run inference"
            : e.status === 502
            ? "ARC serving shim unreachable — is arc_shim.py running and ARC_HOSTGATED_SHIM_URL correct?"
            : `infer failed (HTTP ${e.status})`
          : e instanceof Error
          ? e.message
          : "infer failed";
      patchLastAssistantError(setTurns, message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3">
      <div className="rounded bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-700 px-3 py-2 text-xs text-indigo-800 dark:text-indigo-300">
        <strong>Host-gated mode.</strong> The model runs on a host; this device is the
        per-response safety gate + attestor. The prompt is proxied to the ARC serving shim,
        which generates upstream, gates the full response on this device's silicon, and returns
        the answer with an <span className="font-mono">arc</span> verdict + attestation receipt.
        Non-streaming (gating is per-response), so there is no live tok/s telemetry.
      </div>

      <div
        ref={scrollRef}
        className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3 h-[24rem] overflow-y-auto space-y-3 text-sm"
      >
        {turns.length === 0 && (
          <p className="text-slate-400 dark:text-slate-500 text-center py-12">
            No messages yet. Send a prompt to generate via the host model, gated by this device.
          </p>
        )}
        {turns.map((t, i) => (
          <HostgatedBubble key={i} turn={t} />
        ))}
      </div>

      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
      >
        <input
          type="text"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="Ask the host model (gated by this device)…"
          disabled={busy}
          className="flex-1 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={busy || !prompt.trim()}
          className="rounded bg-slate-900 dark:bg-purple-600 text-white px-4 py-2 text-sm font-medium hover:bg-slate-800 dark:hover:bg-purple-500 disabled:opacity-40 inline-flex items-center gap-2"
        >
          {busy ? (
            <>
              <Spinner />
              <span>generating…</span>
            </>
          ) : (
            "Send"
          )}
        </button>
      </form>
    </div>
  );
}

// patchLastAssistant fills the trailing pending assistant turn with a reply.
function patchLastAssistant(
  setTurns: React.Dispatch<React.SetStateAction<HostgatedTurn[]>>,
  reply: HostgatedReply,
) {
  setTurns((prev) => {
    const next = prev.slice();
    for (let i = next.length - 1; i >= 0; i--) {
      if (next[i].role === "assistant" && next[i].pending) {
        next[i] = {
          ...next[i],
          pending: false,
          text: reply.text,
          arc: reply.arc,
          wallMs: reply.wallMs,
        };
        break;
      }
    }
    return next;
  });
}

function patchLastAssistantError(
  setTurns: React.Dispatch<React.SetStateAction<HostgatedTurn[]>>,
  message: string,
) {
  setTurns((prev) => {
    const next = prev.slice();
    for (let i = next.length - 1; i >= 0; i--) {
      if (next[i].role === "assistant" && next[i].pending) {
        next[i] = { ...next[i], pending: false, error: message };
        break;
      }
    }
    return next;
  });
}

function HostgatedBubble({ turn }: { turn: HostgatedTurn }) {
  const isUser = turn.role === "user";
  return (
    <div className={isUser ? "text-right" : "text-left"}>
      <div
        className={
          "inline-block max-w-[85%] rounded px-3 py-2 whitespace-pre-wrap text-left " +
          (isUser
            ? "bg-slate-900 dark:bg-purple-600 text-white"
            : "bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-slate-100")
        }
      >
        {turn.text || (turn.pending ? <span className="opacity-60">…</span> : turn.error ? "" : "")}
      </div>
      {!isUser && turn.error && (
        <div className="mt-1 text-xs text-red-600 dark:text-red-400">⚠ {turn.error}</div>
      )}
      {!isUser && turn.arc && <HostgatedArcSummary arc={turn.arc} />}
      {!isUser && turn.wallMs !== undefined && !turn.error && (
        <div className="mt-1 text-xs text-slate-400 dark:text-slate-500">
          <span className="font-mono">{(turn.wallMs / 1000).toFixed(1)}s</span> wall (host generate + device gate)
        </div>
      )}
    </div>
  );
}

// HostgatedArcSummary surfaces the host-gated verdict at a glance: the gate
// decision (ALLOW/FLAG/BLOCK), whether a device-rooted attestation receipt is
// present, and pcache skip%/speedup when the shim passes cache stats through.
// The full `arc` JSON stays available via the existing ArcVerdict <details>.
function HostgatedArcSummary({ arc }: { arc: HostgatedArc }) {
  const decision = arc.decision ?? (arc.blocked ? "BLOCK" : "ALLOW");
  const blocked = arc.blocked === true || decision === "BLOCK";
  const badgeCls = blocked
    ? "bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300"
    : decision === "FLAG"
    ? "bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300"
    : "bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300";
  const hasReceipt = isReceiptPresent(arc.receipt);
  const pcache = arc.pcache;
  const skip = typeof pcache?.skip_pct === "number" ? pcache.skip_pct : undefined;
  const speedup = typeof pcache?.speedup === "number" ? pcache.speedup : undefined;
  return (
    <div className="mt-1 flex flex-col gap-1 text-xs">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className={`rounded px-2 py-0.5 font-semibold ${badgeCls}`}>
          gate: {decision}
        </span>
        <span
          className={
            "rounded px-2 py-0.5 font-medium " +
            (hasReceipt
              ? "bg-green-50 dark:bg-green-900/40 text-green-700 dark:text-green-300"
              : "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300")
          }
          title={
            hasReceipt
              ? "The shim attached a device-rooted attestation receipt to this response."
              : "No attestation receipt present (the device's attest hardware may be unavailable off the full-core gold)."
          }
        >
          {hasReceipt ? "receipt ✓" : "receipt: n/a"}
        </span>
        {arc.gate_backend && (
          <span className="text-slate-400 dark:text-slate-500 font-mono">{arc.gate_backend}</span>
        )}
        {(skip !== undefined || speedup !== undefined) && (
          <span className="rounded px-2 py-0.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 font-medium">
            pcache{skip !== undefined ? ` · skip ${skip.toFixed(0)}%` : ""}
            {speedup !== undefined ? ` · ${speedup.toFixed(2)}×` : ""}
          </span>
        )}
      </div>
      <ArcVerdict arc={arc} />
    </div>
  );
}

// isReceiptPresent decides whether the arc block carries a usable attestation
// receipt (a signed bundle, not a null placeholder).
function isReceiptPresent(receipt: unknown): boolean {
  if (!receipt || typeof receipt !== "object") return false;
  // Any non-empty object counts; a signed bundle has session_hash + a signature
  // field, but we don't require a specific shape (forward-compat with the shim).
  return Object.keys(receipt as Record<string, unknown>).length > 0;
}

// --- inference telemetry presentation ---------------------------------------

// A small Tailwind-only spinner (no extra deps); animate-spin is a built-in
// Tailwind utility. Used in the Send button and the live strip.
function Spinner({ className = "" }: { className?: string }) {
  return (
    <span
      role="status"
      aria-label="generating"
      className={
        "inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent align-[-0.125em] " +
        className
      }
    />
  );
}

// fmtSecs renders an elapsed ms value as seconds with one decimal (e.g. "12.3s").
function fmtSecs(ms: number): string {
  return `${(ms / 1000).toFixed(1)}s`;
}

// fmtTokPerSec renders tokens-so-far ÷ elapsed-seconds. Always an estimate (the
// token count is estimated), so callers append the "(est.)" label themselves.
function fmtTokPerSec(tokens: number, elapsedMs: number): string {
  if (elapsedMs <= 0 || tokens <= 0) return "0";
  const tps = tokens / (elapsedMs / 1000);
  return tps >= 10 ? tps.toFixed(0) : tps.toFixed(1);
}

// Derive the token figure for a turn: exact iff the stream carried a usage
// count (never today), otherwise the labeled estimate from streamed chars.
function turnTokens(m: TurnMetrics): { count: number; exact: boolean } {
  if (typeof m.tokensExact === "number") return { count: m.tokensExact, exact: true };
  return { count: estimateTokens(m.chars), exact: false };
}

// LiveTelemetry is the in-flight strip: animated "generating…", a live elapsed
// timer, TTFT once the first token lands, and a running ~token count + ~tok/s.
// All timings are exact wall-clock; the token figures are estimates (labeled).
function LiveTelemetry({ m }: { m: TurnMetrics }) {
  // Elapsed is recomputed on every render; the parent's 200ms ticker forces
  // those renders while generating, so this stays live without storing time.
  const elapsedMs = performance.now() - m.startMs;
  const { count, exact } = turnTokens(m);
  const label = exact ? "" : " (est.)";
  return (
    <div className="rounded border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
      <span className="inline-flex items-center gap-1.5 font-medium text-slate-700 dark:text-slate-200">
        <Spinner className="text-blue-600 dark:text-purple-400" />
        generating…
      </span>
      <Metric label="elapsed" value={fmtSecs(elapsedMs)} mono />
      <Metric
        label="TTFT"
        value={m.ttftMs !== undefined ? fmtSecs(m.ttftMs) : "waiting…"}
        mono={m.ttftMs !== undefined}
      />
      <Metric label="tokens" value={`${exact ? "" : "~"}${count}${label}`} mono />
      <Metric
        label="tok/s"
        value={`${exact ? "" : "~"}${fmtTokPerSec(count, elapsedMs)}${label}`}
        mono
      />
    </div>
  );
}

// Metric is a compact label:value pair used in the live strip and the summary.
function Metric({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <span className="inline-flex items-baseline gap-1">
      <span className="uppercase tracking-wide text-[10px] text-slate-400 dark:text-slate-500">
        {label}
      </span>
      <span className={(mono ? "font-mono " : "") + "text-slate-700 dark:text-slate-200"}>
        {value}
      </span>
    </span>
  );
}

// TurnSummary renders the final stats on a completed assistant turn: total wall
// time (exact), ~tokens, average ~tok/s, and TTFT. Token figures are labeled as
// estimates unless the stream carried an exact usage count.
function TurnSummary({ m }: { m: TurnMetrics }) {
  if (m.endMs === undefined) return null;
  const { count, exact } = turnTokens(m);
  const label = exact ? "" : " (est.)";
  return (
    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
      <Metric label="wall" value={fmtSecs(m.endMs)} mono />
      <Metric label="tokens" value={`${exact ? "" : "~"}${count}${label}`} mono />
      <Metric label="avg tok/s" value={`${exact ? "" : "~"}${fmtTokPerSec(count, m.endMs)}${label}`} mono />
      <Metric
        label="TTFT"
        value={m.ttftMs !== undefined ? fmtSecs(m.ttftMs) : "—"}
        mono={m.ttftMs !== undefined}
      />
    </div>
  );
}

function ChatBubble({ turn }: { turn: ChatTurn }) {
  const isUser = turn.role === "user";
  return (
    <div className={isUser ? "text-right" : "text-left"}>
      <div
        className={
          "inline-block max-w-[85%] rounded px-3 py-2 whitespace-pre-wrap text-left " +
          (isUser
            ? "bg-slate-900 dark:bg-purple-600 text-white"
            : "bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-slate-100")
        }
      >
        {turn.text || (turn.pending ? <span className="opacity-60">…</span> : "")}
      </div>
      {!isUser && (turn.arc !== undefined || turn.status !== undefined) && (
        <div className="mt-1">
          {turn.arc !== undefined && <ArcVerdict arc={turn.arc} />}
          {turn.status !== undefined && (
            <span className="ml-2 text-xs text-slate-400 dark:text-slate-500 font-mono">
              http {turn.status}
            </span>
          )}
        </div>
      )}
      {/* Final inference telemetry for a completed assistant turn. */}
      {!isUser && !turn.pending && turn.metrics?.endMs !== undefined && (
        <TurnSummary m={turn.metrics} />
      )}
      {/* Receipt-transparency lifecycle: reply → signed receipt → registered →
          verified. Only present when the toggle was ON for this reply. */}
      {!isUser && turn.register && <RegisterStatus state={turn.register} />}
    </div>
  );
}

// RegisterToggle is the opt-in switch for the receipt-transparency demo. Default
// OFF; flipping it ON makes every subsequent reply's signed receipt register
// into the SA transparency log (verifiable in /receipts).
function RegisterToggle({
  enabled,
  onChange,
}: {
  enabled: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-xs cursor-pointer select-none">
      <input
        type="checkbox"
        checked={enabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-slate-300 dark:border-slate-600 accent-purple-600"
      />
      <span className="text-slate-700 dark:text-slate-300">
        <span className="font-medium">Register receipt with the SA</span>{" "}
        <span className="text-slate-500 dark:text-slate-400">(transparency log)</span>
        <span className="block text-slate-500 dark:text-slate-400 mt-0.5">
          Opt-in for demos: each reply's on-die signed receipt is recorded in the{" "}
          <Link to="/receipts" className="text-blue-600 dark:text-blue-400 hover:underline">
            attestation receipts
          </Link>{" "}
          log and verified server-side. Off by default.
        </span>
      </span>
    </label>
  );
}

// RegisterStatus renders one reply's transparency-log lifecycle inline under the
// bubble: registering… → "✓ Registered as receipt <id>" with links to the
// receipt + its verify page and a verified / config-authentic badge, or a clear
// inline error (the chat is never broken by a failed register).
function RegisterStatus({ state }: { state: RegisterState }) {
  if (state.phase === "registering") {
    return (
      <div className="mt-1 flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
        <Spinner />
        <span>registering receipt with the SA…</span>
      </div>
    );
  }
  if (state.phase === "error") {
    return (
      <div className="mt-1 text-xs text-red-600 dark:text-red-400">
        ⚠ receipt not registered: {state.message}
      </div>
    );
  }
  const r = state.result;
  // LEAD with the DEVICE-ROOTED verdict: the receipt's ECDSA-P256 signature over
  // session_hash verifies against the signer kit's enrollment-pinned pubkey AND
  // the session_hash commits the reported merge config (config_verified). This is
  // the real per-kit proof. The shared-key HMAC (`verified`) is DEMOTED to a
  // secondary line — it reads false for a device-bound canary by design (the SA
  // does not hold the kit's per-device HMAC key), so a "false" there is "n/a",
  // not a failure.
  const deviceRooted = r.device_rooted === true;
  const ecdsaValid = r.ecdsa_valid === true;
  const cfgOk = r.config_verified === true;
  const hmacOk = r.verified;
  const idShort = r.id.slice(0, 12);
  return (
    <div className="mt-1 flex flex-col gap-1 text-xs">
      {/* Line 1: registration + the headline device-rooted badge. */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="text-green-700 dark:text-green-400 font-medium">
          ✓ Registered as receipt
        </span>
        <Link
          to={`/receipts/${r.id}`}
          className="font-mono text-blue-600 dark:text-blue-400 hover:underline"
          title={r.id}
        >
          {idShort}…
        </Link>
        {deviceRooted ? (
          <span
            className="rounded px-1.5 py-0.5 font-semibold bg-green-50 dark:bg-green-900/40 text-green-700 dark:text-green-300"
            title={
              "ECDSA-P256 signature over session_hash verified against the kit's enrollment-pinned public key" +
              (r.signer_device_id ? ` (${r.signer_device_id})` : "")
            }
          >
            ✓ Verified — device-rooted (ECDSA)
          </span>
        ) : ecdsaValid ? (
          // Signature is good but config didn't fully verify — still device-signed.
          <span
            className="rounded px-1.5 py-0.5 font-semibold bg-amber-50 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300"
            title="ECDSA signature verified, but the merge-config recompute did not confirm — see verify page"
          >
            device-signed (ECDSA) ✓ · config unconfirmed
          </span>
        ) : (
          <span
            className="rounded px-1.5 py-0.5 font-semibold bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300"
            title={r.ecdsa_reason || "no device-rooted ECDSA signature to verify"}
          >
            device-rooted: n/a
          </span>
        )}
        {/* config authenticity is supporting evidence for the device-rooted verdict */}
        {cfgOk && (
          <span className="rounded px-1.5 py-0.5 font-medium bg-green-50 dark:bg-green-900/40 text-green-700 dark:text-green-300">
            config-authentic ✓
          </span>
        )}
        <Link
          to={`/receipts/${r.id}/verify`}
          className="text-blue-600 dark:text-blue-400 hover:underline"
        >
          verify →
        </Link>
      </div>
      {/* Line 2: shared-key HMAC, demoted + explained so a "false" is not read as
          a failure. */}
      <div className="text-[11px] text-slate-500 dark:text-slate-400">
        {hmacOk ? (
          <>shared-key HMAC: match ✓</>
        ) : (
          <>
            shared-key HMAC: n/a — device-bound per-kit key (the device proof is
            the ECDSA signature above)
          </>
        )}
      </div>
    </div>
  );
}

// ArcVerdict renders the kit's `arc` safety block. Shape is daemon-defined; we
// show a compact decision badge if present and the raw JSON for transparency.
function ArcVerdict({ arc }: { arc: unknown }) {
  const decision =
    arc && typeof arc === "object" && "decision" in arc
      ? String((arc as { decision: unknown }).decision)
      : undefined;
  const badgeCls =
    decision === "BLOCK"
      ? "bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300"
      : decision === "FLAG"
      ? "bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300"
      : "bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300";
  return (
    <details className="inline-block text-left align-top">
      <summary className="cursor-pointer list-none">
        <span className={`rounded px-2 py-0.5 text-xs font-semibold ${badgeCls}`}>
          arc{decision ? `: ${decision}` : ""} (advisory)
        </span>
      </summary>
      <pre className="mt-1 max-w-md overflow-x-auto rounded bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-2 text-[11px] font-mono">
        {JSON.stringify(arc, null, 2)}
      </pre>
    </details>
  );
}

// patchPending applies fn to the last pending assistant turn (immutably).
function patchPending(turns: ChatTurn[], fn: (t: ChatTurn) => ChatTurn): ChatTurn[] {
  for (let i = turns.length - 1; i >= 0; i--) {
    if (turns[i].role === "assistant" && turns[i].pending) {
      const next = turns.slice();
      next[i] = fn(turns[i]);
      return next;
    }
  }
  return turns;
}
